import React from "react";
import { Link } from "react-router-dom";
import { BRAND } from "@/lib/site";
import SEO from "@/components/SEO";

const LAST_UPDATED = "December 2025";

const Section = ({ n, title, children }) => (
  <section className="border-t border-zinc-200 pt-10 mt-10 first:border-t-0 first:pt-0 first:mt-0">
    <div className="tech-label text-[#C2410C]">// {n}</div>
    <h2 className="font-display text-2xl md:text-3xl font-bold uppercase tracking-tight mt-3 leading-tight">
      {title}
    </h2>
    <div className="mt-6 text-zinc-700 text-base leading-relaxed space-y-4">
      {children}
    </div>
  </section>
);

export default function Terms() {
  return (
    <div data-testid="terms-page">
      <SEO
        title="Terms of Use"
        description="Terms governing the use of the Concrete Insight UK website."
      />

      <section className="border-b border-zinc-200">
        <div className="max-w-4xl mx-auto px-6 pt-24 pb-16 md:pt-32 md:pb-20">
          <div className="tech-label">// Legal, Terms of Use</div>
          <h1 className="hero-heading text-4xl sm:text-5xl md:text-6xl mt-6">
            Terms of Use
          </h1>
          <div className="mt-6 text-[11px] font-mono uppercase tracking-[0.2em] text-zinc-500">
            Last updated: {LAST_UPDATED}
          </div>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-6 py-16 md:py-24">
        <Section n="01" title="Who these terms apply to">
          <p>
            These terms apply to your use of the {BRAND.name} website. By using the site you accept these terms.
          </p>
          <p>
            <strong>{BRAND.name}</strong> is a company registered in England and Wales. Our registered office is at {BRAND.address.line1}, {BRAND.address.line2}, {BRAND.address.postcode}.
          </p>
        </Section>

        <Section n="02" title="What the site is">
          <p>
            The site provides information about our concrete diagnostic and repair services. It is a source of information, not a formal engineering opinion or specification. Nothing on this site should be relied on in place of a site-specific survey, testing and structural assessment carried out by a suitably qualified engineer.
          </p>
          <p>
            Product references (Fosroc Renderoc, Nitoprime, Dekguard, Nitomortar and others) are used as illustrations of the type of material we specify. Always consult the manufacturer&apos;s current technical data sheet before use. All product trademarks belong to their respective owners.
          </p>
        </Section>

        <Section n="03" title="Enquiries and quotations">
          <p>
            Submitting the enquiry form does not create a contract. It is an invitation for us to respond. Any survey, testing or repair work we carry out will be governed by a separate written engagement and, where relevant, a formal scope of works.
          </p>
        </Section>

        <Section n="04" title="Content and intellectual property">
          <p>
            All content on this site, including text, photographs of our own work, page layouts and graphics, is owned by {BRAND.name} unless credited otherwise. You may view and print pages for your own personal or business reference. You may not republish, redistribute or use our content for commercial purposes without our written permission.
          </p>
          <p>
            Photographs of finished products, technical illustrations of manufacturer processes and manufacturer trademarks remain the property of the respective manufacturer.
          </p>
        </Section>

        <Section n="05" title="Limitation of liability">
          <p>
            We take reasonable care to keep the information on this site accurate and current, but we do not guarantee that it is complete, accurate or up to date at all times. To the fullest extent permitted by law, we exclude liability for any loss arising from your use of, or reliance on, information on this site.
          </p>
          <p>
            Nothing in these terms limits our liability for death or personal injury caused by negligence, for fraud, or for any other liability that cannot be limited under English law.
          </p>
        </Section>

        <Section n="06" title="Links to other sites">
          <p>
            Where we link to external sites (for example, manufacturer product pages), we do so for reference only. We are not responsible for the content or availability of those sites.
          </p>
        </Section>

        <Section n="07" title="Governing law">
          <p>
            These terms are governed by the laws of England and Wales. Any dispute will be dealt with exclusively by the courts of England and Wales.
          </p>
        </Section>

        <Section n="08" title="Contact">
          <p>
            Questions about these terms can be sent to{" "}
            <a href={`mailto:${BRAND.email}`} className="text-[#C2410C] underline">
              {BRAND.email}
            </a>.{" "}
            See also our{" "}
            <Link to="/privacy" className="text-[#C2410C] underline">
              Privacy Policy
            </Link>{" "}
            and{" "}
            <Link to="/cookies" className="text-[#C2410C] underline">
              Cookie Policy
            </Link>.
          </p>
        </Section>
      </div>
    </div>
  );
}
